@let current = currentField();
@let family = color();
@let isBlocked = blocked();
@let isDefault = type() === cardTypes.DEFAULT;
@let data = table();

<card-base
    [field]="field()"
    [favoriteField]="favoriteField()"
    [trashField]="trashField()"
    [type]="type()"
    [disabled]="isBlocked"
    [selected]="selected()"
    [showAi]="false"
    [block]="true">

    <!-- Zona 1 do rail: "Aparência", com a cor do cabeçalho, o estilo e as linhas
         alternadas no mesmo menu. -->
    <div
        card-toolbar
        class="table-rail">
        @if (isDefault) {
            <div class="rail-chip-wrapper">
                <button
                    type="button"
                    class="rail-chip"
                    [class.open]="look.isOpen()"
                    [class.default-disabled]="isBlocked"
                    [attr.aria-label]="'document.edit.railLook.appearance' | translate"
                    [raEditTooltip]="look.isOpen() ? null : { text: ('document.edit.railLook.appearance' | translate), position: 'left' }"
                    [raLookPanel]="lookTpl"
                    lookPanelTitle="document.edit.railLook.appearance"
                    [lookPanelItemId]="field()?._id"
                    [lookPanelDisabled]="isBlocked"
                    #look="lookPanel"
                    (click)="look.toggle()">
                    <ra-icon icon="palette" varColor="currentColor" [size]="22" />
                </button>

                <ng-template #lookTpl>
                    <div class="look-panel-body">
                        <p class="look-title">{{ 'document.edit.tableCard.color' | translate }}</p>
                        <div class="look-swatches">
                            @for (option of colorOptions; track option.key) {
                                <button
                                    type="button"
                                    class="look-swatch"
                                    [class.current]="family.key === option.key"
                                    [style.background-color]="option.accent"
                                    [attr.aria-label]="option.labelKey | translate"
                                    [attr.aria-pressed]="family.key === option.key"
                                    [raEditTooltip]="{ text: (option.labelKey | translate), position: 'top' }"
                                    (click)="selectColor(option.key)">
                                </button>
                            }
                            <custom-color-swatch
                                [value]="family.accent"
                                [current]="family.key === 'custom'"
                                [label]="'document.edit.calloutCard.colorCustom' | translate"
                                (preview)="colorPreview.set($event)"
                                (choose)="chooseCustomColor($event)" />
                        </div>

                        <span class="look-divider"></span>
                        <p class="look-title">{{ 'document.edit.tableCard.style' | translate }}</p>
                        <div class="look-grid">
                            @for (option of styleOptions; track option.key) {
                                <button
                                    type="button"
                                    class="look-option"
                                    [class.current]="styleKey() === option.key"
                                    (click)="selectStyle(option.key)">
                                    <span
                                        aria-hidden="true"
                                        [class]="'table-sample table-sample-large table-sample-' + option.key"
                                        [style.--table-accent]="family.statNumber"
                                        [style.--table-fill]="family.fill">
                                        <i></i><i></i><i></i>
                                    </span>
                                    <span>{{ option.labelKey | translate }}</span>
                                </button>
                            }
                        </div>

                        <span class="look-divider"></span>
                        <button
                            type="button"
                            class="look-switch"
                            role="switch"
                            [attr.aria-checked]="zebra()"
                            (click)="toggleZebra()">
                            <span>{{ 'document.edit.tableCard.zebra' | translate }}</span>
                            <span class="look-toggle" [class.on]="zebra()" aria-hidden="true"></span>
                        </button>
                    </div>
                </ng-template>
            </div>
        }
    </div>

    <!-- Tamanho da letra da tabela (só tamanho: a cor é a da tabela). -->

    @if (isDefault) {
        <!-- A tabela como imprime, editável no lugar. -->
        <div
            class="table-editor"
            [style.font-family]="editDataService.documentFontFamily()"
            (focusin)="onFocusIn()"
            (focusout)="onFocusOut($event)"
            (click)="$event.stopPropagation()">

            <!-- A tabela ocupa a largura toda; o "+" da coluna mora no canto direito do
                 cabeçalho, então só o cabeçalho cede espaço para ele. -->
            <!-- A lista de arrasto é o quadro inteiro (não o tbody): ele se estende até a margem
                 onde ficam a alça e a lixeira, senão o CDK não reordena (ver .table-frame). -->
            <div
                class="table-frame"
                cdkDropList
                [cdkDropListData]="data.rows"
                [cdkDropListDisabled]="isBlocked"
                (cdkDropListDropped)="dropRow($event)">
                <table
                    [class]="'table table-' + styleKey() + (zebra() ? ' table-zebra' : '')"
                    [class.table-sized]="!!data.widths"
                    [class.table-resizing]="resizing() !== null"
                    [style.--table-accent]="family.statNumber"
                    [style.--table-ink]="family.statInk"
                    [style.--table-fill]="family.fill"
                    [style.--table-on-accent]="family.onAccent"
                    [style.--item-size]="textEditing.sizeRem(current)">
                    @if (data.widths) {
                        <colgroup>
                            @for (width of data.widths; track $index) {
                                <col [style.width.%]="width" />
                            }
                        </colgroup>
                    }
                    <thead>
                        <tr>
                            @for (column of data.columns; track $index; let ci = $index) {
                                @let isLast = ci === data.columns.length - 1;
                                <th [class.removable]="canRemoveColumn()" [class.has-add]="isLast">
                                    <div
                                        class="cell-text"
                                        [attr.data-cell]="'h-' + ci"
                                        [attr.data-placeholder]="'document.edit.tableCard.columnPlaceholder' | translate: { n: ci + 1 }"
                                        [attr.contenteditable]="isBlocked ? 'false' : 'plaintext-only'"
                                        [tableCellText]="column"
                                        (cellTextChange)="onHeaderInput(ci, $event)"
                                        (keydown)="onCellKeydown($event, -1, ci)"></div>

                                    <!-- Excluir a coluna: lixeira discreta, só no hover do cabeçalho. -->
                                    @if (canRemoveColumn()) {
                                        <button
                                            type="button"
                                            tabindex="-1"
                                            class="cell-action column-remove"
                                            [disabled]="isBlocked"
                                            [attr.aria-label]="'document.edit.tableCard.removeColumn' | translate"
                                            [raEditTooltip]="{ text: ('document.edit.tableCard.removeColumn' | translate), position: 'top' }"
                                            (click)="removeColumn(ci)">
                                            <ra-icon icon="trash" varColor="currentColor" [size]="14" />
                                        </button>
                                    }

                                    @if (isLast) {
                                        <button
                                            type="button"
                                            class="add-column"
                                            [disabled]="isBlocked || !canAddColumn()"
                                            [attr.aria-label]="'document.edit.tableCard.addColumn' | translate"
                                            [raEditTooltip]="{ text: (canAddColumn() ? ('document.edit.tableCard.addColumn' | translate) : ('document.edit.tableCard.columnLimit' | translate)), position: 'top' }"
                                            (click)="addColumn()">
                                            <ra-icon icon="add" varColor="currentColor" [size]="14" />
                                        </button>
                                    }

                                    <!-- Borda da coluna: arrastar ajusta a largura; duplo clique volta ao automático. -->
                                    @if (!isBlocked && ci < data.columns.length - 1) {
                                        <span
                                            class="col-resize"
                                            [class.active]="resizing() === ci"
                                            [attr.aria-label]="'document.edit.tableCard.resizeColumn' | translate"
                                            [raEditTooltip]="resizing() !== null ? null : { text: ('document.edit.tableCard.resizeColumn' | translate), position: 'top' }"
                                            (pointerdown)="startResize($event, ci)"
                                            (dblclick)="resetWidths($event)"></span>
                                    }
                                </th>
                            }
                        </tr>
                    </thead>
                    <tbody>
                        @for (row of data.rows; track $index; let ri = $index) {
                            <!-- Onde a folha do PDF vira no meio da tabela (dado da prévia). -->
                            @if (rowPageBreaks()[ri]; as pageNumber) {
                                <tr class="page-break-row" aria-hidden="true">
                                    <td [attr.colspan]="data.columns.length">
                                        <span class="page-break-line">
                                            <span class="page-break-label">
                                                {{ 'document.edit.pdfPreview.pageBreakAt' | translate: { page: pageNumber } }}
                                            </span>
                                        </span>
                                    </td>
                                </tr>
                            }
                            <tr cdkDrag cdkDragLockAxis="y">
                                @for (cell of row; track $index; let ci = $index) {
                                    <td>
                                        <!-- Alça e lixeira da linha: empilhadas na margem do card, à
                                             esquerda da tabela, só no hover da linha. -->
                                        @if (ci === 0) {
                                            <span class="row-tools">
                                                <span
                                                    class="cell-action row-grip"
                                                    cdkDragHandle
                                                    [attr.aria-label]="'document.edit.tableCard.dragRow' | translate"
                                                    [raEditTooltip]="{ text: ('document.edit.tableCard.dragRow' | translate), position: 'left' }">
                                                    <ra-icon icon="menu" varColor="currentColor" [size]="12" />
                                                </span>
                                                <button
                                                    type="button"
                                                    tabindex="-1"
                                                    class="cell-action row-remove"
                                                    [disabled]="isBlocked"
                                                    [attr.aria-label]="'document.edit.tableCard.removeRow' | translate"
                                                    [raEditTooltip]="{ text: ('document.edit.tableCard.removeRow' | translate), position: 'left' }"
                                                    (click)="removeRow(ri)">
                                                    <ra-icon icon="trash" varColor="currentColor" [size]="12" />
                                                </button>
                                            </span>
                                        }
                                        <div
                                            class="cell-text"
                                            [attr.data-cell]="ri + '-' + ci"
                                            [attr.contenteditable]="isBlocked ? 'false' : 'plaintext-only'"
                                            [tableCellText]="cell"
                                            (cellTextChange)="onCellInput(ri, ci, $event)"
                                            (keydown)="onCellKeydown($event, ri, ci)"></div>
                                    </td>
                                }
                            </tr>
                        }
                    </tbody>
                </table>

            </div>

            <button type="button" class="add-row" [disabled]="isBlocked || !canAddRow()" (click)="addRow()">
                <ra-icon icon="add" varColor="currentColor" [size]="14" />
                <span>{{ 'document.edit.tableCard.addRow' | translate }}</span>
            </button>
        </div>
    } @else {
        <table-view
            [textSize]="textEditing.sizeRem(current)"
            [value]="current.value"
            [styleKey]="current.configs?.tableStyle"
            [zebra]="current.configs?.tableZebra"
            [color]="current.configs?.calloutColor"
            [maxRows]="4" />
    }
</card-base>
